
import { requestForToken } from '../../../utils/firebase';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import apiCall from '../../../utils/api';
import { facilitySignup } from '../../facility/actions';
import { fetchUserProfile } from '../../user/actions'; // Consolidated import

interface LoginState {
  isLoggedIn: boolean;
  email: string | null;
  error: string | null;
  isLoading: boolean;
  accessToken: string | null;
  refreshToken: string | null;
}

const initialState: LoginState = {
  isLoggedIn: false,
  email: null,
  error: null,
  isLoading: false,
  accessToken: null,
  refreshToken: null,
};

// Async Thunk for login
export const loginUser = createAsyncThunk(
  'login/loginUser',
  async ({ email, password, rememberMe }: { email: string; password: string; rememberMe?: boolean }, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiCall<{ data: any }>('POST', '/auth/login/', { email, password });
      
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }
      
      dispatch(fetchUserProfile());
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message || 'Login failed');
    }
  }
);

export const refreshToken = createAsyncThunk(
  'login/refreshToken',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { refreshToken } = (getState() as any).login;
      const response = await apiCall<{ data: any }>('POST', '/auth/login/refresh/', { refresh: refreshToken });
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data.message || 'Refresh token failed');
    }
  }
);

export const validateToken = createAsyncThunk(
  'login/validateToken',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const { accessToken } = (getState() as any).login;
      if (!accessToken) {
        return rejectWithValue('No access token found');
      }
      await dispatch(fetchUserProfile());
      await apiCall('GET', '/auth/validate-token');
      // requestForToken()
      // Dispatch fetchUserProfile after successful token validation
      return;
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        try {
          await dispatch(refreshToken());
          // After successful token refresh, dispatch fetchUserProfile again
          // await dispatch(fetchUserProfile());
        } catch (e) {
          return rejectWithValue('Token validation failed after refresh');
        }
      }
      else {
        return rejectWithValue('Token validation failed');
      }
    }
  }
);

export const loginSlice = createSlice({
  name: 'login',
  initialState,
  reducers: {
    logout: (state) => {
      state.isLoggedIn = false;
      state.email = null;
      state.error = null;
      state.accessToken = null;
      state.refreshToken = null;
    },
    updateAccessToken: (state, action: PayloadAction<string>) => {
      state.accessToken = action.payload;
    },
    setTokens: (state, action: PayloadAction<{ access: string; refresh: string }>) => {
      state.accessToken = action.payload.access;
      state.refreshToken = action.payload.refresh;
      state.isLoggedIn = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action: PayloadAction<{ access: string; refresh: string }>) => {
        state.isLoading = false;
        state.isLoggedIn = true;
        state.accessToken = action.payload.access;
        state.refreshToken = action.payload.refresh;
        state.error = null;
        requestForToken();
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoggedIn = false;
        state.email = null;
        state.error = action.payload as string;
        state.accessToken = null;
        state.refreshToken = null;
      })
      .addCase(refreshToken.fulfilled, (state, action: PayloadAction<{ access: string; refresh: string }>) => {
        state.accessToken = action.payload.access;
        state.refreshToken = action.payload.refresh;
      })
      .addCase(refreshToken.rejected, (state) => {
        state.isLoggedIn = false;
        state.email = null;
        state.error = null;
        state.accessToken = null;
        state.refreshToken = null;
      })
      .addCase(validateToken.rejected, (state) => {
        state.isLoggedIn = false;
        state.email = null;
        state.error = null;
        state.accessToken = null;
        state.refreshToken = null;
      })
      .addCase(facilitySignup.fulfilled, (state, action) => {
        state.isLoggedIn = true;
        state.accessToken = action.payload.token.access;
        state.refreshToken = action.payload.token.refresh;
      });
  },
});

export const { logout, updateAccessToken, setTokens } = loginSlice.actions;

export default loginSlice.reducer;

